import { ensureTransaction } from './data';

/**
 * Transitions
 *
 * These strings must sync with values defined in Flex API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const TRANSITION_REQUEST_PAYMENT = 'transition/request-payment';
export const TRANSITION_CREATE_PAYMENT_INTENT = 'transition/create-payment-intent';
export const TRANSITION_EXPIRE_PAYMENT_INTENT = 'transition/expire-payment-intent';
// Deliberate customer-triggered aborts of an unpaid transaction, executed by
// the server as operator (abort-unpaid-transaction endpoint + initiate dedupe).
// Same effect as the 15-minute timer twins, just immediate.
export const TRANSITION_ABORT_PAYMENT_INTENT = 'transition/abort-payment-intent';
export const TRANSITION_ABORT_PAYMENT = 'transition/abort-payment';
export const TRANSITION_PAYMENT_EXPIRE = 'transition/expire-payment';
export const TRANSITION_CONFIRM_PAYMENT = 'transition/confirm-payment';
export const TRANSITION_AUTO_CONFIRM_PAYMENT = 'transition/auto-confirm-payment';
export const TRANSITION_UNSTUCK_AUTO_CONFIRM_PAYMENT = 'transition/unstuck-auto-confirm-payment';
// Operator twin of confirm-payment, fired by the server when a PaymentIntent is
// authorized but the client never got back to confirm it (see the
// rescueAuthorizedPayment task). Lands in the same state as confirm-payment, so
// a rescued booking shows up as a normal request for the provider.
export const TRANSITION_UNSTUCK_CONFIRM_PAYMENT = 'transition/unstuck-confirm-payment';
export const TRANSITION_DECLINE = 'transition/decline';
export const TRANSITION_EXPIRE = 'transition/expire';
export const TRANSITION_WITHDRAW = 'transition/withdraw';
export const TRANSITION_NO_REPLY_EXPIRE = 'transition/no-reply-expire';
export const TRANSITION_ACCEPT = 'transition/accept';
export const TRANSITION_AUTO_ACCEPT = 'transition/auto-accept';
export const TRANSITION_CANCEL = 'transition/cancel';
export const TRANSITION_PROVIDER_CANCEL = 'transition/provider-cancel';
export const TRANSITION_CUSTOMER_CANCEL = 'transition/customer-cancel';
export const TRANSITION_COMPLETE = 'transition/complete';

export const TRANSITION_REVIEW_1_BY_PROVIDER = 'transition/review-1-by-provider';
export const TRANSITION_REVIEW_2_BY_PROVIDER = 'transition/review-2-by-provider';
export const TRANSITION_REVIEW_1_BY_CUSTOMER = 'transition/review-1-by-customer';
export const TRANSITION_REVIEW_2_BY_CUSTOMER = 'transition/review-2-by-customer';
export const TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD = 'transition/expire-customer-review-period';
export const TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD = 'transition/expire-provider-review-period';

/**
 * Actors
 *
 * There are 4 different actors that might initiate transitions:
 */

// Roles of actors that perform transaction transitions
export const TX_TRANSITION_ACTOR_CUSTOMER = 'customer';
export const TX_TRANSITION_ACTOR_PROVIDER = 'provider';
export const TX_TRANSITION_ACTOR_SYSTEM = 'system';
export const TX_TRANSITION_ACTOR_OPERATOR = 'operator';

export const TX_TRANSITION_ACTORS = [
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  TX_TRANSITION_ACTOR_SYSTEM,
  TX_TRANSITION_ACTOR_OPERATOR,
];

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
const STATE_INITIAL = 'initial';
const STATE_PENDING_PAYMENT_INTENT = 'pending-payment-intent';
const STATE_PENDING_PAYMENT = 'pending-payment';
const STATE_PAYMENT_EXPIRED = 'payment-expired';
const STATE_CONFIRM_PAYMENT = 'confirm-payment';
const STATE_PREAUTHORIZED = 'preauthorized';
const STATE_DECLINED = 'declined';
const STATE_WITHDRAWN = 'withdrawn';
const STATE_ACCEPTED = 'accepted';
const STATE_CANCELED = 'canceled';
const STATE_DELIVERED = 'delivered';
const STATE_REVIEWED = 'reviewed';
const STATE_REVIEWED_BY_CUSTOMER = 'reviewed-by-customer';
const STATE_REVIEWED_BY_PROVIDER = 'reviewed-by-provider';

/**
 * Description of transaction process
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const defaultStateDescription = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking-process/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: STATE_INITIAL,

  // States
  states: {
    [STATE_INITIAL]: {
      on: {
        [TRANSITION_REQUEST_PAYMENT]: STATE_PENDING_PAYMENT_INTENT,
      },
    },
    [STATE_PENDING_PAYMENT_INTENT]: {
      on: {
        [TRANSITION_CREATE_PAYMENT_INTENT]: STATE_PENDING_PAYMENT,
        [TRANSITION_EXPIRE_PAYMENT_INTENT]: STATE_PAYMENT_EXPIRED,
        [TRANSITION_ABORT_PAYMENT_INTENT]: STATE_PAYMENT_EXPIRED,
      },
    },
    [STATE_PENDING_PAYMENT]: {
      on: {
        [TRANSITION_PAYMENT_EXPIRE]: STATE_PAYMENT_EXPIRED,
        [TRANSITION_ABORT_PAYMENT]: STATE_PAYMENT_EXPIRED,
        [TRANSITION_CONFIRM_PAYMENT]: STATE_PREAUTHORIZED,
        [TRANSITION_UNSTUCK_CONFIRM_PAYMENT]: STATE_PREAUTHORIZED,
        [TRANSITION_AUTO_CONFIRM_PAYMENT]: STATE_CONFIRM_PAYMENT,
      },
    },
    [STATE_PAYMENT_EXPIRED]: {},
    [STATE_PREAUTHORIZED]: {
      on: {
        [TRANSITION_DECLINE]: STATE_DECLINED,
        [TRANSITION_WITHDRAW]: STATE_WITHDRAWN,
        [TRANSITION_EXPIRE]: STATE_DECLINED,
        [TRANSITION_NO_REPLY_EXPIRE]: STATE_DECLINED,
        [TRANSITION_ACCEPT]: STATE_ACCEPTED,
      },
    },
    [STATE_CONFIRM_PAYMENT]: {
      on: {
        [TRANSITION_AUTO_ACCEPT]: STATE_ACCEPTED,
        [TRANSITION_UNSTUCK_AUTO_CONFIRM_PAYMENT]: STATE_ACCEPTED,
      },
    },
    [STATE_DECLINED]: {},
    [STATE_WITHDRAWN]: {},
    [STATE_ACCEPTED]: {
      on: {
        [TRANSITION_COMPLETE]: STATE_DELIVERED,
        [TRANSITION_CANCEL]: STATE_CANCELED,
        [TRANSITION_CUSTOMER_CANCEL]: STATE_CANCELED,
        [TRANSITION_PROVIDER_CANCEL]: STATE_CANCELED,
      },
    },
    [STATE_CANCELED]: {},
    [STATE_DELIVERED]: {
      on: {
        [TRANSITION_REVIEW_1_BY_CUSTOMER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_REVIEW_1_BY_PROVIDER]: STATE_REVIEWED_BY_PROVIDER,
      },
    },
    [STATE_REVIEWED_BY_CUSTOMER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_PROVIDER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED_BY_PROVIDER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_CUSTOMER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED]: { type: 'final' },
  },
};

// Note: currently we assume that state description doesn't contain nested states.
const statesFromStateDescription = (description) => description.states || {};

// Get all the transitions from states object in an array
const getTransitions = (states) => {
  const stateNames = Object.keys(states);

  const transitionsReducer = (transitionArray, name) => {
    const stateTransitions = states[name] && states[name].on;
    const transitionKeys = stateTransitions ? Object.keys(stateTransitions) : [];
    return [
      ...transitionArray,
      ...transitionKeys.map((key) => ({ key, value: stateTransitions[key] })),
    ];
  };

  return stateNames.reduce(transitionsReducer, []);
};

// This is a list of all the transitions that this app should be able to handle.
export const TRANSITIONS = getTransitions(statesFromStateDescription(defaultStateDescription)).map(
  (t) => t.key
);

// This function returns a function that has given stateDesc in scope chain.
export const getTransitionsToStateFn = (stateDesc) => (state) =>
  getTransitions(statesFromStateDescription(stateDesc))
    .filter((t) => state.includes(t.value))
    .map((t) => t.key);

// Get all the transitions that lead to specified state.
const getTransitionsToState = getTransitionsToStateFn(defaultStateDescription);

// This is needed to fetch transactions that need response from provider.
// I.e. transactions which provider needs to accept or decline
export const transitionsToRequestedForProvider = getTransitionsToState([
  STATE_REVIEWED_BY_CUSTOMER,
  STATE_PREAUTHORIZED,
]);
export const transitionsToRequestedForCustomer = getTransitionsToState([
  STATE_REVIEWED_BY_PROVIDER,
]);

/**
 * Helper functions to figure out if transaction is in a specific state.
 * State is based on lastTransition given by transaction object and state description.
 */

const txLastTransition = (tx) => ensureTransaction(tx).attributes.lastTransition;

export const txIsPendingStripePaymentIntent = (tx) =>
  getTransitionsToState(STATE_PENDING_PAYMENT_INTENT).includes(txLastTransition(tx));

export const txIsPendingPayment = (tx) =>
  getTransitionsToState(STATE_PENDING_PAYMENT).includes(txLastTransition(tx));

export const txIsPaymentExpired = (tx) =>
  getTransitionsToState(STATE_PAYMENT_EXPIRED).includes(txLastTransition(tx));

// Note: state name used in Marketplace API docs (and here) is actually preauthorized
// However, word "requested" is used in many places so that we decided to keep it.
export const txIsRequested = (tx) =>
  getTransitionsToState(STATE_PREAUTHORIZED).includes(txLastTransition(tx));

export const txIsAutoConfirmedPayment = (tx) =>
  getTransitionsToState(STATE_CONFIRM_PAYMENT).includes(txLastTransition(tx));

export const txIsDeclined = (tx) =>
  getTransitionsToState(STATE_DECLINED).includes(txLastTransition(tx));

export const txIsAccepted = (tx) =>
  getTransitionsToState(STATE_ACCEPTED).includes(txLastTransition(tx));

export const txIsAutoConfirmPayment = (tx) =>
  txLastTransition(tx) === TRANSITION_AUTO_CONFIRM_PAYMENT;

export const txIsAutoAccepted = (tx) => txLastTransition(tx) === TRANSITION_AUTO_ACCEPT;

export const txIsCanceled = (tx) =>
  getTransitionsToState(STATE_CANCELED).includes(txLastTransition(tx));

export const txIsProviderCanceled = (tx) =>
  tx.attributes.lastTransition === TRANSITION_PROVIDER_CANCEL;

export const txIsCustomerCanceled = (tx) =>
  tx.attributes.lastTransition === TRANSITION_CUSTOMER_CANCEL;

export const txIsWithdrawn = (tx) => tx.attributes.lastTransition === TRANSITION_WITHDRAW;

export const txIsOperatorCanceled = (tx) => tx.attributes.lastTransition === TRANSITION_CANCEL;

export const txIsDelivered = (tx) =>
  getTransitionsToState(STATE_DELIVERED).includes(txLastTransition(tx));

const firstReviewTransitions = [
  ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
  ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
];
export const txIsInFirstReview = (tx) => firstReviewTransitions.includes(txLastTransition(tx));

export const txIsReviewedByCustomer = (tx) =>
  getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(txLastTransition(tx));

export const txIsReviewedByProvider = (tx) =>
  getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(txLastTransition(tx));

export const txIsInFirstReviewBy = (tx, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(txLastTransition(tx))
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(txLastTransition(tx));

export const txIsReviewed = (tx) =>
  getTransitionsToState(STATE_REVIEWED).includes(txLastTransition(tx));

/**
 * Helper functions to figure out if transaction has passed a given state.
 * This is based on transitions history given by transaction object.
 */

const txTransitions = (tx) => ensureTransaction(tx).attributes.transitions || [];
const hasPassedTransition = (transitionName, tx) =>
  !!txTransitions(tx).find((t) => t.transition === transitionName);

const hasPassedStateFn = (state) => (tx) =>
  getTransitionsToState(state).filter((t) => hasPassedTransition(t, tx)).length > 0;

export const txHasBeenAccepted = hasPassedStateFn(STATE_ACCEPTED);
export const txHasBeenDelivered = hasPassedStateFn(STATE_DELIVERED);

/**
 * Other transaction related utility functions
 */

export const transitionIsReviewed = (transition) =>
  getTransitionsToState(STATE_REVIEWED).includes(transition);

export const transitionIsFirstReviewedBy = (transition, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(transition)
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(transition);

export const getReview1Transition = (isCustomer) =>
  isCustomer ? TRANSITION_REVIEW_1_BY_CUSTOMER : TRANSITION_REVIEW_1_BY_PROVIDER;

export const getReview2Transition = (isCustomer) =>
  isCustomer ? TRANSITION_REVIEW_2_BY_CUSTOMER : TRANSITION_REVIEW_2_BY_PROVIDER;

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = (transition) => {
  return [
    TRANSITION_CONFIRM_PAYMENT,
    TRANSITION_UNSTUCK_CONFIRM_PAYMENT,
    TRANSITION_AUTO_CONFIRM_PAYMENT,
    TRANSITION_DECLINE,
    TRANSITION_WITHDRAW,
    TRANSITION_EXPIRE,
    TRANSITION_EXPIRE_PAYMENT_INTENT,
    TRANSITION_ABORT_PAYMENT_INTENT,
    TRANSITION_PAYMENT_EXPIRE,
    TRANSITION_ABORT_PAYMENT,
    TRANSITION_ACCEPT,
    TRANSITION_AUTO_ACCEPT,
    TRANSITION_CANCEL,
    TRANSITION_PROVIDER_CANCEL,
    TRANSITION_CUSTOMER_CANCEL,
    TRANSITION_COMPLETE,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = (transition) => {
  return [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = (transition) => {
  return [TRANSITION_REVIEW_1_BY_PROVIDER, TRANSITION_REVIEW_2_BY_PROVIDER].includes(transition);
};

export const getUserTxRole = (currentUserId, transaction) => {
  const tx = ensureTransaction(transaction);
  const customer = tx.customer;
  if (currentUserId && currentUserId.uuid && tx.id && customer.id) {
    // user can be either customer or provider
    return currentUserId.uuid === customer.id.uuid
      ? TX_TRANSITION_ACTOR_CUSTOMER
      : TX_TRANSITION_ACTOR_PROVIDER;
  } else {
    throw new Error(`Parameters for "userIsCustomer" function were wrong.
      currentUserId: ${currentUserId}, transaction: ${transaction}`);
  }
};

export const txRoleIsProvider = (userRole) => userRole === TX_TRANSITION_ACTOR_PROVIDER;
export const txRoleIsCustomer = (userRole) => userRole === TX_TRANSITION_ACTOR_CUSTOMER;

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = (transition) => {
  return [
    TRANSITION_REQUEST_PAYMENT,
    TRANSITION_CREATE_PAYMENT_INTENT,
    TRANSITION_CONFIRM_PAYMENT,
    TRANSITION_AUTO_CONFIRM_PAYMENT,
    TRANSITION_ACCEPT,
  ].includes(transition);
};

export const EXPRESS_BOOKING = 'expressBooking';
export const REQUEST_BOOKING = 'bookingRequest';
